import axios, {AxiosInstance, AxiosRequestConfig, AxiosResponse, CancelToken} from 'axios';
import {SafeDigsClientBase, isAxiosError, throwException} from './SafeDigsClientBase';
import {MNullable} from '../Toolkit/Utils';
import MNSwagClientConfiguration from '../Toolkit/Api/NSwagClientConfiguration';

export class SafeDigClient extends SafeDigsClientBase {
    private readonly instance: AxiosInstance;
    private readonly baseUrl: string;
    protected jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;

    constructor(configuration: MNSwagClientConfiguration, baseUrl?: string, instance?: AxiosInstance) {

        super(configuration);

        this.instance = instance ? instance : axios.create();

        this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl('');
    }

    count(id: number, estimate: string, cancelToken?: CancelToken | undefined): Promise<any> {
        let url_ = this.baseUrl + '/SafeDig/GetCount/{id}/{estimate}';
        url_ = url_.replace('{id}', encodeURIComponent('' + id));
        url_ = url_.replace('{estimate}', encodeURIComponent('' + estimate));
        url_ = url_.replace(/[?&]$/, '');

        let options_: AxiosRequestConfig = {
            method: 'GET',
            url: url_,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'text/plain',
                'X-Waws-Unencoded-Url': ''
            },
            cancelToken
        };

        return this.transformOptions(options_).then(transformedOptions_ => {
            return this.instance.request(transformedOptions_);
        }).catch((_error: any) => {
            if(isAxiosError(_error) && _error.response) {
                return _error.response;
            } else {
                throw _error;
            }
        }).then((_response: AxiosResponse) => {
            return this.processCount(_response);
        });
    }

    protected processCount(response: AxiosResponse): Promise<any> {
        const status = response.status;
        let _headers: any = {};
        if(response.headers && typeof response.headers === 'object') {
            for(let k in response.headers) {
                if(response.headers.hasOwnProperty(k)) {
                    _headers[k] = response.headers[k];
                }
            }
        }
        if(status === 404) {
            const _responseText = response.data;
            let result404: any = null;
            let resultData404 = _responseText;
            result404 = resultData404 !== undefined ? resultData404 : null as any;

            return throwException('Not Found', status, _responseText, _headers, result404);

        } else if(status === 200) {
            const _responseText = response.data;
            let result200: any = null;
            let resultData200 = _responseText;
            result200 = resultData200 !== undefined ? resultData200 : null as any;

            return Promise.resolve<any>(result200);

        } else if(status !== 200 && status !== 204) {
            const _responseText = response.data;
            return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }
        return Promise.resolve<any>(null as any);
    }

    /**
     * @return Success
     */
    get(safeDigId: number, cancelToken?: CancelToken | undefined): Promise<any> {
        let url_ = this.baseUrl + '/SafeDig/safedig?id={safeDigId}';
        if(safeDigId === undefined || safeDigId === null) {
            throw new Error('The parameter \'safeDigId\' must be defined.');
        }
        url_ = url_.replace('{safeDigId}', encodeURIComponent('' + safeDigId));
        url_ = url_.replace(/[?&]$/, '');

        let options_: AxiosRequestConfig = {
            method: 'GET',
            url: url_,
            headers: {
                'Accept': 'text/plain'
            },
            cancelToken
        };

        return this.transformOptions(options_).then(transformedOptions_ => {
            return this.instance.request(transformedOptions_);
        }).catch((_error: any) => {
            if(isAxiosError(_error) && _error.response) {
                return _error.response;
            } else {
                throw _error;
            }
        }).then((_response: AxiosResponse) => {
            return this.processGet(_response);
        });
    }

    protected processGet(response: AxiosResponse): Promise<any> {
        const status = response.status;
        let _headers: any = {};
        if(response.headers && typeof response.headers === 'object') {
            for(let k in response.headers) {
                if(response.headers.hasOwnProperty(k)) {
                    _headers[k] = response.headers[k];
                }
            }
        }
        if(status === 404) {
            const _responseText = response.data;
            let result404: any = null;
            let resultData404 = _responseText;
            result404 = resultData404 !== undefined ? resultData404 : null as any;

            return throwException('Not Found', status, _responseText, _headers, result404);

        } else if(status === 200) {
            const _responseText = response.data;
            let result200: any = null;
            let resultData200 = _responseText;
            result200 = resultData200 !== undefined ? resultData200 : null as any;

            return Promise.resolve<any>(result200);

        } else if(status !== 200 && status !== 204) {
            const _responseText = response.data;
            return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }
        return Promise.resolve<any>(null as any);
    }

    /**
     * @param parentId (optional)
     * @return Success
     */
    getSubJobs(parentId: number | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        let url_ = this.baseUrl + "/SafeDig/GetSubJobs?";
        if (parentId === null)
            throw new Error("The parameter 'parentId' cannot be null.");
        else if (parentId !== undefined)
            url_ += "parentId=" + encodeURIComponent("" + parentId) + "&";
        url_ = url_.replace(/[?&]$/, "");

        let options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: {
            },
            cancelToken
        };

        return this.transformOptions(options_).then(transformedOptions_ => {
            return this.instance.request(transformedOptions_);
        }).catch((_error: any) => {
            if (isAxiosError(_error) && _error.response) {
                return _error.response;
            } else {
                throw _error;
            }
        }).then((_response: AxiosResponse) => {
            return this.processGetSubJobs(_response);
        });
    }

    protected processGetSubJobs(response: AxiosResponse): Promise<void> {
        const status = response.status;
        let _headers: any = {};
        if (response.headers && typeof response.headers === "object") {
            for (let k in response.headers) {
                if (response.headers.hasOwnProperty(k)) {
                    _headers[k] = response.headers[k];
                }
            }
        }
        if (status === 200) {
            const _responseText = response.data;
            let result200: any = null;
            let resultData200 = _responseText;
            result200 = resultData200 !== undefined ? resultData200 : null as any;

            return Promise.resolve<any>(result200);

        } else if (status !== 200 && status !== 204) {
            const _responseText = response.data;
            return throwException("An unexpected server error occurred.", status, _responseText, _headers);
        }
        return Promise.resolve<void>(null as any);
    }

    /**
     * @param body (optional)
     * @return Success
     */
    update(body: UpdateSafeDigRequest | undefined, cancelToken?: CancelToken | undefined): Promise<any> {
        let url_ = this.baseUrl + '/SafeDig/UpdateSafeDig';
        url_ = url_.replace(/[?&]$/, '');

        const content_ = JSON.stringify(body);

        let options_: AxiosRequestConfig = {
            data: content_,
            method: 'PUT',
            url: url_,
            headers: {
                'Content-Type': 'application/json-patch+json',
                'Accept': 'text/plain'
            },
            cancelToken
        };

        return this.transformOptions(options_).then(transformedOptions_ => {
            return this.instance.request(transformedOptions_);
        }).catch((_error: any) => {
            if(isAxiosError(_error) && _error.response) {
                return _error.response;
            } else {
                throw _error;
            }
        }).then((_response: AxiosResponse) => {
            return this.processUpdate(_response);
        });
    }

    protected processUpdate(response: AxiosResponse): Promise<any> {
        const status = response.status;
        let _headers: any = {};
        if(response.headers && typeof response.headers === 'object') {
            for(let k in response.headers) {
                if(response.headers.hasOwnProperty(k)) {
                    _headers[k] = response.headers[k];
                }
            }
        }
        if(status === 400) {
            const _responseText = response.data;
            let result400: any = null;
            let resultData400 = _responseText;
            result400 = resultData400 !== undefined ? resultData400 : null as any;

            return throwException('Bad Request', status, _responseText, _headers, result400);

        } else if(status === 404) {
            const _responseText = response.data;
            let result404: any = null;
            let resultData404 = _responseText;
            result404 = resultData404 !== undefined ? resultData404 : null as any;

            return throwException('Not Found', status, _responseText, _headers, result404);

        } else if(status === 409) {
            const _responseText = response.data;
            let result409: any = null;
            let resultData409 = _responseText;
            result409 = resultData409 !== undefined ? resultData409 : null as any;

            return throwException('Conflict', status, _responseText, _headers, result409);

        } else if(status === 200) {
            const _responseText = response.data;
            let result200: any = null;
            let resultData200 = _responseText;
            result200 = resultData200 !== undefined ? resultData200 : null as any;

            return Promise.resolve<any>(result200);

        } else if(status !== 200 && status !== 204) {
            const _responseText = response.data;
            return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }
        return Promise.resolve<any>(null as any);
    }

    /**
     * @return Success
     */
    getAll(cancelToken?: CancelToken | undefined): Promise<any> {
        let url_ = this.baseUrl + '/SafeDig/GetAll';
        url_ = url_.replace(/[?&]$/, '');

        let options_: AxiosRequestConfig = {
            method: 'GET',
            url: url_,
            headers: {
                'Accept': 'text/plain'
            },
            cancelToken
        };

        return this.transformOptions(options_).then(transformedOptions_ => {
            return this.instance.request(transformedOptions_);
        }).catch((_error: any) => {
            if(isAxiosError(_error) && _error.response) {
                return _error.response;
            } else {
                throw _error;
            }
        }).then((_response: AxiosResponse) => {
            return this.processGetAll(_response);
        });
    }

    protected processGetAll(response: AxiosResponse): Promise<any> {
        const status = response.status;
        let _headers: any = {};
        if(response.headers && typeof response.headers === 'object') {
            for(let k in response.headers) {
                if(response.headers.hasOwnProperty(k)) {
                    _headers[k] = response.headers[k];
                }
            }
        }
        if(status === 200) {
            const _responseText = response.data;
            let result200: any = null;
            let resultData200 = _responseText;
            result200 = resultData200 !== undefined ? resultData200 : null as any;

            return Promise.resolve<any>(result200);

        } else if(status !== 200 && status !== 204) {
            const _responseText = response.data;
            return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }
        return Promise.resolve<any>(null as any);
    }

    /**
     * @param body (optional)
     * @return Success
     */
    post(body: CreateSafeDigRequest | undefined, cancelToken?: CancelToken | undefined): Promise<any> {
        let url_ = this.baseUrl + '/SafeDig/CreateDig';
        url_ = url_.replace(/[?&]$/, '');

        const content_ = JSON.stringify(body);

        let options_: AxiosRequestConfig = {
            data: content_,
            method: 'POST',
            url: url_,
            headers: {
                'Content-Type': 'application/json-patch+json',
                'Accept': 'text/plain'
            },
            cancelToken
        };

        return this.transformOptions(options_).then(transformedOptions_ => {
            return this.instance.request(transformedOptions_);
        }).catch((_error: any) => {
            if(isAxiosError(_error) && _error.response) {
                return _error.response;
            } else {
                throw _error;
            }
        }).then((_response: AxiosResponse) => {
            return this.processPost(_response);
        });
    }

    protected processPost(response: AxiosResponse): Promise<any> {
        const status = response.status;
        let _headers: any = {};
        if(response.headers && typeof response.headers === 'object') {
            for(let k in response.headers) {
                if(response.headers.hasOwnProperty(k)) {
                    _headers[k] = response.headers[k];
                }
            }
        }
        if(status === 400) {
            const _responseText = response.data;
            let result400: any = null;
            let resultData400 = _responseText;
            result400 = resultData400 !== undefined ? resultData400 : null as any;

            return throwException('Bad Request', status, _responseText, _headers, result400);

        } else if(status === 404) {
            const _responseText = response.data;
            let result404: any = null;
            let resultData404 = _responseText;
            result404 = resultData404 !== undefined ? resultData404 : null as any;

            return throwException('Not Found', status, _responseText, _headers, result404);

        } else if(status === 409) {
            const _responseText = response.data;
            let result409: any = null;
            let resultData409 = _responseText;
            result409 = resultData409 !== undefined ? resultData409 : null as any;

            return throwException('Conflict', status, _responseText, _headers, result409);

        } else if(status === 200) {
            const _responseText = response.data;
            let result200: any = null;
            let resultData200 = _responseText;
            result200 = resultData200 !== undefined ? resultData200 : null as any;

            return Promise.resolve<any>(result200);

        } else if(status !== 200 && status !== 204) {
            const _responseText = response.data;
            return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }
        return Promise.resolve<any>(null as any);
    }

    /**
     * @return Success
     */
    getMessages(safeDigId: number, cancelToken?: CancelToken | undefined): Promise<any> {
        let url_ = this.baseUrl + '/SafeDig/GetStatusMessages?id={safeDigTaskId}';
        if(safeDigId === undefined || safeDigId === null) {
            throw new Error('The parameter \'safeDigTaskId\' must be defined.');
        }
        url_ = url_.replace('{safeDigTaskId}', encodeURIComponent('' + safeDigId));
        url_ = url_.replace(/[?&]$/, '');

        let options_: AxiosRequestConfig = {
            method: 'GET',
            url: url_,
            headers: {
                'Accept': 'text/plain'
            },
            cancelToken
        };

        return this.transformOptions(options_).then(transformedOptions_ => {
            return this.instance.request(transformedOptions_);
        }).catch((_error: any) => {
            if(isAxiosError(_error) && _error.response) {
                return _error.response;
            } else {
                throw _error;
            }
        }).then((_response: AxiosResponse) => {
            return this.processGetMessages(_response);
        });
    }

    protected processGetMessages(response: AxiosResponse): Promise<any> {
        const status = response.status;
        let _headers: any = {};
        if(response.headers && typeof response.headers === 'object') {
            for(let k in response.headers) {
                if(response.headers.hasOwnProperty(k)) {
                    _headers[k] = response.headers[k];
                }
            }
        }
        if(status === 404) {
            const _responseText = response.data;
            let result404: any = null;
            let resultData404 = _responseText;
            result404 = resultData404 !== undefined ? resultData404 : null as any;

            return throwException('Not Found', status, _responseText, _headers, result404);

        } else if(status === 200) {
            const _responseText = response.data;
            let result200: any = null;
            let resultData200 = _responseText;
            result200 = resultData200 !== undefined ? resultData200 : null as any;

            return Promise.resolve<any>(result200);

        } else if(status !== 200 && status !== 204) {
            const _responseText = response.data;
            return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }
        return Promise.resolve<any>(null as any);
    }

    getNotes(safeDigId: number, cancelToken?: CancelToken | undefined): Promise<any> {
        let url_ = this.baseUrl + '/SafeDig/GetNotes?id={safeDigTaskId}';
        if(safeDigId === undefined || safeDigId === null) {
            throw new Error('The parameter \'safeDigTaskId\' must be defined.');
        }
        url_ = url_.replace('{safeDigTaskId}', encodeURIComponent('' + safeDigId));
        url_ = url_.replace(/[?&]$/, '');

        let options_: AxiosRequestConfig = {
            method: 'GET',
            url: url_,
            headers: {
                'Accept': 'text/plain'
            },
            cancelToken
        };

        return this.transformOptions(options_).then(transformedOptions_ => {
            return this.instance.request(transformedOptions_);
        }).catch((_error: any) => {
            if(isAxiosError(_error) && _error.response) {
                return _error.response;
            } else {
                throw _error;
            }
        }).then((_response: AxiosResponse) => {
            return this.processGetNotes(_response);
        });
    }

    protected processGetNotes(response: AxiosResponse): Promise<any> {
        const status = response.status;
        let _headers: any = {};
        if(response.headers && typeof response.headers === 'object') {
            for(let k in response.headers) {
                if(response.headers.hasOwnProperty(k)) {
                    _headers[k] = response.headers[k];
                }
            }
        }
        if(status === 404) {
            const _responseText = response.data;
            let result404: any = null;
            let resultData404 = _responseText;
            result404 = resultData404 !== undefined ? resultData404 : null as any;

            return throwException('Not Found', status, _responseText, _headers, result404);

        } else if(status === 200) {
            const _responseText = response.data;
            let result200: any = null;
            let resultData200 = _responseText;
            result200 = resultData200 !== undefined ? resultData200 : null as any;

            return Promise.resolve<any>(result200);

        } else if(status !== 200 && status !== 204) {
            const _responseText = response.data;
            return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }
        return Promise.resolve<any>(null as any);
    }

    send(body: SafeDigSendRequest | undefined, cancelToken?: CancelToken | undefined): Promise<any> {
        let url_ = this.baseUrl + '/SafeDig/SendFiles';
        url_ = url_.replace(/[?&]$/, '');

        const content_ = JSON.stringify(body);

        let options_: AxiosRequestConfig = {
            data: content_,
            method: 'POST',
            url: url_,
            headers: {
                'Content-Type': 'application/json-patch+json',
                'Accept': 'text/plain'
            },
            cancelToken
        };

        return this.transformOptions(options_).then(transformedOptions_ => {
            return this.instance.request(transformedOptions_);
        }).catch((_error: any) => {
            if(isAxiosError(_error) && _error.response) {
                return _error.response;
            } else {
                throw _error;
            }
        }).then((_response: AxiosResponse) => {
            return this.processSend(_response);
        });
    }

    protected processSend(response: AxiosResponse): Promise<any> {
        const status = response.status;
        let _headers: any = {};
        if(response.headers && typeof response.headers === 'object') {
            for(let k in response.headers) {
                if(response.headers.hasOwnProperty(k)) {
                    _headers[k] = response.headers[k];
                }
            }
        }
        if(status === 500) {
            const _responseText = response.data;
            let result500: any = null;
            let resultData500 = _responseText;
            result500 = resultData500 !== undefined ? resultData500 : null as any;

            return throwException('Bad Request', status, _responseText, _headers, result500);

        } else if(status === 200) {
            const _responseText = response.data;
            let result200: any = null;
            let resultData200 = _responseText;
            result200 = resultData200 !== undefined ? resultData200 : null as any;

            return Promise.resolve<any>(result200);

        } else if(status !== 200 && status !== 204) {
            const _responseText = response.data;
            return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }
        return Promise.resolve<any>(null as any);
    }

    addNote(body: NoteCreateDto | undefined, cancelToken?: CancelToken | undefined): Promise<any> {
        let url_ = this.baseUrl + '/SafeDig/AddNotes';
        url_ = url_.replace(/[?&]$/, '');

        const content_ = JSON.stringify(body);

        let options_: AxiosRequestConfig = {
            data: content_,
            method: 'POST',
            url: url_,
            headers: {
                'Content-Type': 'application/json-patch+json',
                'Accept': 'text/plain'
            },
            cancelToken
        };

        return this.transformOptions(options_).then(transformedOptions_ => {
            return this.instance.request(transformedOptions_);
        }).catch((_error: any) => {
            if(isAxiosError(_error) && _error.response) {
                return _error.response;
            } else {
                throw _error;
            }
        }).then((_response: AxiosResponse) => {
            return this.processAddNote(_response);
        });
    }

    protected processAddNote(response: AxiosResponse): Promise<any> {
        const status = response.status;
        let _headers: any = {};
        if(response.headers && typeof response.headers === 'object') {
            for(let k in response.headers) {
                if(response.headers.hasOwnProperty(k)) {
                    _headers[k] = response.headers[k];
                }
            }
        }
        if(status === 500) {
            const _responseText = response.data;
            let result500: any = null;
            let resultData500 = _responseText;
            result500 = resultData500 !== undefined ? resultData500 : null as any;

            return throwException('Bad Request', status, _responseText, _headers, result500);

        } else if(status === 200) {
            const _responseText = response.data;
            let result200: any = null;
            let resultData200 = _responseText;
            result200 = resultData200 !== undefined ? resultData200 : null as any;

            return Promise.resolve<any>(result200);

        } else if(status !== 200 && status !== 204) {
            const _responseText = response.data;
            return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }
        return Promise.resolve<any>(null as any);
    }

    updateSupervision(body: SafeDigUpdateSupervisionRequest | undefined, cancelToken?: CancelToken | undefined): Promise<any> {
        let url_ = this.baseUrl + '/SafeDig/UpdateSupervisionDate';
        url_ = url_.replace(/[?&]$/, '');

        const content_ = JSON.stringify(body);

        let options_: AxiosRequestConfig = {
            data: content_,
            method: 'PUT',
            url: url_,
            headers: {
                'Content-Type': 'application/json-patch+json',
                'Accept': 'text/plain'
            },
            cancelToken
        };

        return this.transformOptions(options_).then(transformedOptions_ => {
            return this.instance.request(transformedOptions_);
        }).catch((_error: any) => {
            if(isAxiosError(_error) && _error.response) {
                return _error.response;
            } else {
                throw _error;
            }
        }).then((_response: AxiosResponse) => {
            return this.processUpdateSupervision(_response);
        });
    }

    protected processUpdateSupervision(response: AxiosResponse): Promise<any> {
        const status = response.status;
        let _headers: any = {};
        if(response.headers && typeof response.headers === 'object') {
            for(let k in response.headers) {
                if(response.headers.hasOwnProperty(k)) {
                    _headers[k] = response.headers[k];
                }
            }
        }
        if(status === 500) {
            const _responseText = response.data;
            let result500: any = null;
            let resultData500 = _responseText;
            result500 = resultData500 !== undefined ? resultData500 : null as any;

            return throwException('Bad Request', status, _responseText, _headers, result500);

        } else if(status === 200) {
            const _responseText = response.data;
            let result200: any = null;
            let resultData200 = _responseText;
            result200 = resultData200 !== undefined ? resultData200 : null as any;

            return Promise.resolve<any>(result200);

        } else if(status !== 200 && status !== 204) {
            const _responseText = response.data;
            return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }
        return Promise.resolve<any>(null as any);
    }

    releaseJob(safeDigId: number, user: string, cancelToken?: CancelToken | undefined): Promise<any> {
        let url_ = this.baseUrl + '/SafeDig/ReleaseJob?id={safeDigId}&user={user}';
        if(safeDigId === undefined || safeDigId === null) {
            throw new Error('The parameter \'safeDigId\' must be defined.');
        }
        url_ = url_.replace('{safeDigId}', encodeURIComponent('' + safeDigId));
        url_ = url_.replace('{user}', encodeURIComponent('' + user));
        url_ = url_.replace(/[?&]$/, '');

        let options_: AxiosRequestConfig = {
            method: 'PUT',
            url: url_,
            headers: {
                'Content-Type': 'application/json-patch+json',
                'Accept': 'text/plain'
            },
            cancelToken
        };

        return this.transformOptions(options_).then(transformedOptions_ => {
            return this.instance.request(transformedOptions_);
        }).catch((_error: any) => {
            if(isAxiosError(_error) && _error.response) {
                return _error.response;
            } else {
                throw _error;
            }
        }).then((_response: AxiosResponse) => {
            return this.processReleaseJob(_response);
        });
    }

    protected processReleaseJob(response: AxiosResponse): Promise<any> {
        const status = response.status;
        let _headers: any = {};
        if(response.headers && typeof response.headers === 'object') {
            for(let k in response.headers) {
                if(response.headers.hasOwnProperty(k)) {
                    _headers[k] = response.headers[k];
                }
            }
        }
        if(status === 500) {
            const _responseText = response.data;
            let result500: any = null;
            let resultData500 = _responseText;
            result500 = resultData500 !== undefined ? resultData500 : null as any;

            return throwException('Bad Request', status, _responseText, _headers, result500);

        } else if(status === 200) {
            const _responseText = response.data;
            let result200: any = null;
            let resultData200 = _responseText;
            result200 = resultData200 !== undefined ? resultData200 : null as any;

            return Promise.resolve<any>(result200);

        } else if(status !== 200 && status !== 204) {
            const _responseText = response.data;
            return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }
        return Promise.resolve<any>(null as any);
    }

    /**
     * @return Success
     */
    getProjects(cancelToken?: CancelToken | undefined): Promise<any> {
        let url_ = this.baseUrl + '/SafeDig/GetProjects';
        url_ = url_.replace(/[?&]$/, '');

        let options_: AxiosRequestConfig = {
            method: 'GET',
            url: url_,
            headers: {
                'Accept': 'text/plain'
            },
            cancelToken
        };

        return this.transformOptions(options_).then(transformedOptions_ => {
            return this.instance.request(transformedOptions_);
        }).catch((_error: any) => {
            if(isAxiosError(_error) && _error.response) {
                return _error.response;
            } else {
                throw _error;
            }
        }).then((_response: AxiosResponse) => {
            return this.processGetProjects(_response);
        });
    }

    protected processGetProjects(response: AxiosResponse): Promise<any> {
        const status = response.status;
        let _headers: any = {};
        if(response.headers && typeof response.headers === 'object') {
            for(let k in response.headers) {
                if(response.headers.hasOwnProperty(k)) {
                    _headers[k] = response.headers[k];
                }
            }
        }
        if(status === 404) {
            const _responseText = response.data;
            let result404: any = null;
            let resultData404 = _responseText;
            result404 = resultData404 !== undefined ? resultData404 : null as any;

            return throwException('Not Found', status, _responseText, _headers, result404);

        } else if(status === 200) {
            const _responseText = response.data;
            let result200: any = null;
            let resultData200 = _responseText;
            result200 = resultData200 !== undefined ? resultData200 : null as any;

            return Promise.resolve<any>(result200);

        } else if(status !== 200 && status !== 204) {
            const _responseText = response.data;
            return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }
        return Promise.resolve<any>(null as any);
    }

    cancelJob(estimate: string, cancelToken?: CancelToken | undefined): Promise<any> {
        let url_ = this.baseUrl + '/SafeDig/CancelJob?estimate={estimate}';
        url_ = url_.replace('{estimate}', encodeURIComponent('' + estimate));
        url_ = url_.replace(/[?&]$/, '');

        let options_: AxiosRequestConfig = {
            method: 'DELETE',
            url: url_,
            headers: {
                'Accept': 'text/plain'
            },
            cancelToken
        };

        return this.transformOptions(options_).then(transformedOptions_ => {
            return this.instance.request(transformedOptions_);
        }).catch((_error: any) => {
            if(isAxiosError(_error) && _error.response) {
                return _error.response;
            } else {
                throw _error;
            }
        }).then((_response: AxiosResponse) => {
            return this.processCancelJob(_response);
        });
    }

    protected processCancelJob(response: AxiosResponse): Promise<any> {
        const status = response.status;
        let _headers: any = {};
        if(response.headers && typeof response.headers === 'object') {
            for(let k in response.headers) {
                if(response.headers.hasOwnProperty(k)) {
                    _headers[k] = response.headers[k];
                }
            }
        }
        if(status === 404) {
            const _responseText = response.data;
            let result404: any = null;
            let resultData404 = _responseText;
            result404 = resultData404 !== undefined ? resultData404 : null as any;

            return throwException('Not Found', status, _responseText, _headers, result404);

        } else if(status === 200) {
            const _responseText = response.data;
            let result200: any = null;
            let resultData200 = _responseText;
            result200 = resultData200 !== undefined ? resultData200 : null as any;

            return Promise.resolve<any>(result200);

        } else if(status !== 200 && status !== 204) {
            const _responseText = response.data;
            return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }
        return Promise.resolve<any>(null as any);
    }
};

export class UpdateSafeDigRequest implements IUpdateSafeDigRequest {
    safeDigsId: number | undefined;
    project: number | undefined;
    estimate: string | undefined;
    requestor: string | undefined;
    address1: string | undefined;
    address2: string | undefined;
    address3: string | undefined;
    county: string | undefined;
    postcode: string | undefined;
    workDescription: string | undefined;
    northing: number | undefined;
    easting: number | undefined;
    northing2: number | undefined;
    easting2: number | undefined;
    receivedDate: Date | undefined;
    requiredDate: Date | undefined;
    digStatus: number | undefined;
    user: string | undefined;
    parent: number | undefined;

    constructor(data?: IUpdateSafeDigRequest) {
        if(data) {
            for(var property in data) {
                if(data.hasOwnProperty(property)) {
                    (this as any)[property] = (data as any)[property];
                }
            }
        }
    }

    init(_data?: any) {
        if(_data) {
            this.safeDigsId = _data['safeDigsId'];
            this.project = _data['project'];
            this.estimate = _data['estimate'];
            this.requestor = _data['requestor'];
            this.address1 = _data['address1'];
            this.address2 = _data['address2'];
            this.address3 = _data['address3'];
            this.county = _data['county'];
            this.postcode = _data['postcode'];
            this.workDescription = _data['workDescription'];
            this.northing = _data['northing'];
            this.easting = _data['easting'];
            this.northing2 = _data['northing2'];
            this.easting2 = _data['easting2'];
            this.receivedDate = _data['receivedDate'] ? new Date(_data['receivedDate'].toISOString()) : undefined as any;
            this.requiredDate = _data['requiredDate'] ? new Date(_data['requiredDate'].toISOString()) : undefined as any;
            this.digStatus = _data['digStatus'];
            this.user = _data['user'];
            this.parent = _data['parent'];
        }
    }

    static fromJS(data: any): UpdateSafeDigRequest {
        data = typeof data === 'object' ? data : {};
        let result = new UpdateSafeDigRequest();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data['safeDigsId'] = this.safeDigsId;
        data['project'] = this.project;
        data['estimate'] = this.estimate;
        data['requestor'] = this.requestor;
        data['address1'] = this.address1;
        data['address2'] = this.address2;
        data['address3'] = this.address3;
        data['county'] = this.county;
        data['postcode'] = this.postcode;
        data['workDescription'] = this.workDescription;
        data['northing'] = this.northing;
        data['easting'] = this.easting;
        data['northing2'] = this.northing2;
        data['easting2'] = this.easting2;
        data['requiredDate'] = this.requiredDate ? this.requiredDate.toISOString() : undefined as any;
        data['receivedDate'] = this.receivedDate ? this.receivedDate.toISOString() : undefined as any;
        data['digStatus'] = this.digStatus;
        data['user'] = this.user;
        data['parent'] = this.parent;
        return data;
    }
};

export class CreateSafeDigRequest implements ICreateSafeDigRequest {
    estimate: string | undefined;
    project: number | undefined;
    requestor: string | undefined;
    address1: string | undefined;
    address2: string | undefined;
    address3: string | undefined;
    county: string | undefined;
    postcode: string | undefined;
    workDescription: string | undefined;
    northing: number | undefined;
    easting: number | undefined;
    northing2: number | undefined;
    easting2: number | undefined;
    requiredDate: Date | undefined;
    user: string | undefined;
    parent: number | undefined;

    constructor(data?: ICreateSafeDigRequest) {
        if(data) {
            for(var property in data) {
                if(data.hasOwnProperty(property)) {
                    (this as any)[property] = (data as any)[property];
                }
            }
        }
    }

    init(_data?: any) {
        if(_data) {
            this.estimate = _data['estimate'];
            this.project = _data['project'];
            this.requestor = _data['requestor'];
            this.address1 = _data['address1'];
            this.address2 = _data['address2'];
            this.address3 = _data['address3'];
            this.county = _data['county'];
            this.postcode = _data['postcode'];
            this.workDescription = _data['workDescription'];
            this.northing = _data['northing'];
            this.easting = _data['easting'];
            this.northing2 = _data['northing2'];
            this.easting2 = _data['easting2'];
            this.requiredDate = _data['requiredDate'] ? new Date(_data['requiredDate'].toISOString()) : undefined as any;
            this.user = _data['user'];
            this.parent = _data['parent'];
        }
    }

    static fromJS(data: any): CreateSafeDigRequest {
        data = typeof data === 'object' ? data : {};
        let result = new CreateSafeDigRequest();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data['estimate'] = this.estimate;
        data['project'] = this.project;
        data['requestor'] = this.requestor;
        data['address1'] = this.address1;
        data['address2'] = this.address2;
        data['address3'] = this.address3;
        data['county'] = this.county;
        data['postcode'] = this.postcode;
        data['workDescription'] = this.workDescription;
        data['northing'] = this.northing;
        data['easting'] = this.easting;
        data['northing2'] = this.northing2;
        data['easting2'] = this.easting2;
        data['requiredDate'] = this.requiredDate ? this.requiredDate.toISOString() : undefined as any;
        data['user'] = this.user;
        data['parent'] = this.parent
        return data;
    }
}

export class SafeDigSendRequest implements ISafeDigSendRequest {
    safeDigId: number | undefined;
    emailAddresses: string[] | undefined;
    user: string | undefined;

    constructor(data?: ISafeDigSendRequest) {
        if(data) {
            for(var property in data) {
                if(data.hasOwnProperty(property)) {
                    (this as any)[property] = (data as any)[property];
                }
            }
        }
    }

    init(_data?: any) {
        if(_data) {
            this.safeDigId = _data['safeDigId'];
            this.emailAddresses = _data['emailAddresses'];
            this.user = _data['user'];
        }
    }

    static fromJS(data: any): SafeDigSendRequest {
        data = typeof data === 'object' ? data : {};
        let result = new SafeDigSendRequest();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data['safeDigId'] = this.safeDigId;
        data['emailAddresses'] = this.emailAddresses;
        data['user'] = this.user;
        return data;
    }
}

export class SafeDigUpdateSupervisionRequest implements ISafeDigUpdateSupervisionRequest {
    safeDigId: number | undefined;
    supervisionDate: Date | undefined;
    user: string | undefined;

    constructor(data?: ISafeDigUpdateSupervisionRequest) {
        if(data) {
            for(var property in data) {
                if(data.hasOwnProperty(property)) {
                    (this as any)[property] = (data as any)[property];
                }
            }
        }
    }

    init(_data?: any) {
        if(_data) {
            this.safeDigId = _data['safeDigId'];
            this.supervisionDate = _data['supervisionDate'];
            this.user = _data['user'];
        }
    }

    static fromJS(data: any): SafeDigUpdateSupervisionRequest {
        data = typeof data === 'object' ? data : {};
        let result = new SafeDigUpdateSupervisionRequest();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data['safeDigId'] = this.safeDigId;
        data['supervisionDate'] = this.supervisionDate;
        data['user'] = this.user;
        return data;
    }
}

export class NoteCreateDto implements INoteCreateDto {
    safeDigsId: number | undefined;
    noteText: string | undefined;
    user: string | undefined;

    constructor(data?: INoteCreateDto) {
        if(data) {
            for(var property in data) {
                if(data.hasOwnProperty(property)) {
                    (this as any)[property] = (data as any)[property];
                }
            }
        }
    }

    init(_data?: any) {
        if(_data) {
            this.safeDigsId = _data['safeDigId'];
            this.noteText = _data['noteText'];
            this.user = _data['user'];
        }
    }

    static fromJS(data: any): SafeDigSendRequest {
        data = typeof data === 'object' ? data : {};
        let result = new SafeDigSendRequest();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data['safeDigsId'] = this.safeDigsId;
        data['noteText'] = this.noteText;
        data['user'] = this.user;
        return data;
    }
}

export interface ICreateSafeDigRequest {
    estimate: string | undefined;
    project: number | undefined;
    requestor: string | undefined;
    address1: string | undefined;
    address2: string | undefined;
    address3: string | undefined;
    county: string | undefined;
    postcode: string | undefined;
    workDescription: string | undefined;
    northing: number | undefined;
    easting: number | undefined;
    northing2: number | undefined;
    easting2: number | undefined;
    requiredDate: Date | undefined;
    parent: number | undefined;
    user: string | undefined;
};

export interface IUpdateSafeDigRequest {
    safeDigsId: number | undefined;
    project: number | undefined;
    estimate: string | undefined;
    requestor: string | undefined;
    address1: string | undefined;
    address2: string | undefined;
    address3: string | undefined;
    county: string | undefined;
    postcode: string | undefined;
    workDescription: string | undefined;
    northing: number | undefined;
    easting: number | undefined;
    northing2: number | undefined;
    easting2: number | undefined;
    receivedDate: Date | undefined;
    requiredDate: Date | undefined;
    digStatus: number | undefined;
    parent: number | undefined;
    user: string | undefined;
};

export interface ISafeDigSendRequest {
    safeDigId: number | undefined;
    emailAddresses: string[] | undefined;
    user: string | undefined;
}

export interface ISafeDigUpdateSupervisionRequest {
    safeDigId: number | undefined;
    supervisionDate: MNullable<Date> | undefined;
    user: string | undefined;
}

export interface INoteCreateDto {
    safeDigsId: number | undefined;
    noteText: string | undefined;
    user: string | undefined;
}

